import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import CircularProgress from '@mui/material/CircularProgress';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const C = {
  accent: '#2dd4bf', bg: '#f0f4f4', card: '#ffffff',
  cardBorder: 'rgba(0,0,0,0.06)', text: '#1a2e2e', muted: '#5a7a7a',
  shadow: '0 2px 10px rgba(0,0,0,0.07)', danger: '#ef4444',
};
const inp = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f0f4f4', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'default', color: C.text };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const selectStyles = {
  control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '12px', borderRadius: '7px', borderColor: C.cardBorder }),
  valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

const CLAIM_CATEGORIES = [
  { value: '1', label: 'Master' },
  { value: '2', label: 'Transaction' },
  { value: '3', label: 'Accounts' },
  { value: '4', label: 'Reports' },
  { value: '5', label: 'Administration' },
];

const RoleDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  const [isLoading, setIsLoading]           = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage]     = useState('');
  const [selectedCategory, setSelectedCategory] = useState(CLAIM_CATEGORIES[0]);
  const [formData, setFormData] = useState({ Name: '', Claims: [], UpdatedBy: userId });

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchData('RoleApi', `/RoleDetails/${id}`, token)
      .then(res => {
        if (res.responseCode === '1111' && res.response_data) {
          const { RoleVM } = res.response_data;
          setFormData(prev => ({ ...prev, ...RoleVM, Claims: (res.response_data.Claims || []).map(c => ({ ...c })) }));
        }
      })
      .catch(err => console.error('Error fetching role details:', err));
  }, [id]);

  const getClaimRange = () => {
    const start = (parseInt(selectedCategory.value, 10) - 1) * 5;
    return { start, end: start + 4 };
  };

  const handleChangeClaim = (index) => {
    setFormData(prev => {
      const updated = [...prev.Claims];
      updated[index] = { ...updated[index], IsSelected: !updated[index].IsSelected };
      return { ...prev, Claims: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await updateData('RoleApi', `${id}`, { ...formData, UpdatedBy: userId }, token);
      setSuccessMessage('Role updated successfully.');
      history.push(`/roleDetail/${id}`);
    } catch (err) {
      console.error('Error updating role:', err);
      setErrorMessage('Failed to update. Please try again.');
    } finally { setIsLoading(false); }
  };

  const { start, end } = getClaimRange();
  const visibleClaims = formData.Claims.filter((_, i) => i >= start && i <= end);

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AdminPanelSettingsIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Edit Role</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSubmit} disabled={isLoading}
              style={{ height: '28px', padding: '0 16px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' }}>
              {isLoading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : '✓ Update'}
            </button>
            <Link to="/RoleIndex"
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {errorMessage   && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '11px', color: C.danger }}>{errorMessage}</div>}
      {successMessage && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', fontSize: '11px', color: '#16a34a' }}>{successMessage}</div>}

      {/* ── Role Name ── */}
      <div style={card}>
        <div style={cardHead()}>🎭 Role Info</div>
        <div style={{ ...cardBody }}>
          <label style={lbl}>Role Name</label>
          <input type="text" value={formData.Name || ''} readOnly style={inp} />
        </div>
      </div>

      {/* ── Claims ── */}
      <div style={card}>
        <div style={cardHead('#8b5cf6')}>🔑 Claims</div>
        <div style={cardBody}>
          <div style={{ marginBottom: '10px', maxWidth: '260px' }}>
            <label style={lbl}>Category</label>
            <Select options={CLAIM_CATEGORIES} value={selectedCategory} onChange={setSelectedCategory} styles={selectStyles} />
          </div>
          {visibleClaims.length > 0 ? visibleClaims.map((claim, i) => {
            const realIndex = start + i;
            return (
              <div key={realIndex} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.cardBorder}` }}>
                <span style={{ fontSize: '12px', color: C.text }}>{claim.ClaimValue}</span>
                <input type="checkbox" checked={claim.IsSelected} onChange={() => handleChangeClaim(realIndex)}
                  style={{ width: 14, height: 14, cursor: 'pointer', accentColor: C.accent }} />
              </div>
            );
          }) : <p style={{ fontSize: '12px', color: C.muted }}>No claims available.</p>}
        </div>
      </div>

    </div>
  );
};

export default RoleDetail;
